import { useEffect } from "react";
import { useCompanyInfo } from "@/lib/company";

/**
 * V2.18 — Dynamic branding hook. Reads the active brand from `/api/company-info`
 * (served by the whitelabel profile at the backend) and injects:
 *   - the favicon <link rel="icon"> href
 *   - --primary / --accent CSS vars on <html> (HEX → HSL)
 *   - the <title> tag using brand_name + tagline
 *
 * The hook is a no-op until the company-info fetch resolves — defaults from
 * the HTML shell stay visible during that first paint.
 */

function hexToHslTriplet(hex) {
  if (typeof hex !== "string") return null;
  const m = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-f]{6}$/i.test(m)) return null;
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function setFavicon(url) {
  if (!url || typeof document === "undefined") return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
}

export default function useBranding() {
  const company = useCompanyInfo();
  useEffect(() => {
    if (!company || typeof document === "undefined") return;
    const root = document.documentElement;
    const primary = hexToHslTriplet(company.primary_color);
    const accent = hexToHslTriplet(company.accent_color);
    if (primary) root.style.setProperty("--primary", primary);
    if (accent) root.style.setProperty("--accent", accent);
    setFavicon(company.favicon_url);
    if (company.brand_name) {
      const tagline = company.tagline_i18n?.en || company.tagline || "";
      document.title = tagline
        ? `${company.brand_name} — ${tagline}`
        : company.brand_name;
    }
  }, [company]);
}
